<template>
  <div>
    <breadcrumbs :home="product.name + ': Convert to other product'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row no-gutters>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh"/>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col>
              <in-bond v-model="transaction.bonded" @change="changeBond"/>
            </v-col>
          </v-row>

          <cases-bottles :cases.sync="transaction.cases" :bottles.sync="transaction.bottles" :product="product" :availableCount="available" avaiableSuffix="Available"/>
          <v-row>
            <v-col cols="6" sm="3" lg="2"  v-if="company.settings.lotTrackingForProducts && (transaction.bonded || company.settings.lotTrackingForDutyPaid)">
              <v-combobox v-model="transaction.lot" outlined dense label="Lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
            </v-col>
            <v-col cols="12" sm="6" lg="4" v-if="sites.length>1">
              <v-autocomplete outlined dense label="Site" v-model="transaction.siteId" :items="sites" item-value="id" item-text="name"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6" lg="4">
              <v-autocomplete outlined dense label="Convert to product" v-model="transaction.targetProductId" :items="products" item-value="id" item-text="name"
                              no-data-text="No products found" :rules="[fieldRules.required]"
                              persistent-hint :hint="productHint"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InBond from '@/components/InBond'
import CasesBottles from '@/components/CasesBottles'

export default {
  name: 'InventoryProductsConvert',
  mixins: [confirmLeave],
  components: {InBond,CasesBottles},
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      init: true,
      edit: false,
      product: {name:'',sites:[],alcohol:{}},
      allProducts: [],
      valid:true,
      saving:false,
      transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), targetProductId:'',
                    bonded:null, cases:0, bottles: 0, lot:'', comments: '',siteId:''},
      dirty: false,
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.transactionId, this.transaction.bonded ? {B262:true} : undefined);
    },
    productHint() {
      return `Products with spirit type ${this.product.alcohol.name}, at ${this.product.strength}% alc./vol., in ${this.product.bottleSize}ml bottles`;
    },
    currentLots() {
      return this.product.sites.filter(site => site.siteId === this.transaction.siteId &&
                                              (this.transaction.bonded && site.inBondBottles || !this.transaction.bonded && site.dutyPaidBottles))
                                .map(site => site.lot);
    },
    products() {
      return this.allProducts.filter(p => p.id !== this.product.id &&
                                          p.strength === this.product.strength &&
                                          p.bottleSize === this.product.bottleSize &&
                                          p.alcohol.id === this.product.alcohol.id);
    },
    available() {
      let site;
      if (!this.company.settings.lotTrackingForProducts ||
          !this.company.settings.lotTrackingForDutyPaid && !this.transaction.bonded)
        site = this.product.sites.reduce((site, productSite) => {
          if (productSite.siteId === this.transaction.siteId) {
            site.dutyPaidBottles += productSite.dutyPaidBottles;
            site.inBondBottles += productSite.inBondBottles;
          }
          return site;
        }, {dutyPaidBottles:0, inBondBottles:0});
      else
        site = this.product.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lot);
      return (this.transaction.bonded ? site?.inBondBottles : site?.dutyPaidBottles) || 0;
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    this.$lazyListLoad(this.allProducts, 'products2', 'id,name,strength,bottleSize,alcohol{id}', 100, !this.transactionId ? [{field:'active', comparator:'eq', value:'true'}] : [])

    if (this.transactionId) {
      const results = await this.$apiCall(`query InventoryProductsConvert($id:ID!) {
                                                productConvert(id:$id) {
                                                  id,productId,date,bonded,cases,bottles,lot,comments,siteId,targetProductId
                                                }
                                              }`,
                                          {id:this.transactionId});
      if (results.error) {
        this.$nextTick(() => this.dirty = false);
        this.$router.back();
        return;
      }
      this.transaction = results.data.productConvert;
    }
    if (!this.transactionId) {
      this.transaction.siteId = this.sites[0].id;
      this.edit = true;
    }
    await this.refresh();
    this.$nextTick(() => this.dirty = false);
    this.init = false;
  },

	methods: {
    changeBond() {
      if (!this.sites.some(site => site.id === this.transaction.siteId))
        this.transaction.siteId = this.sites[0].id;
      this.$nextTick(() => this.$refs.form.validate());
    },
    async refresh() {
      const results = await this.$apiCall(`query InventoryProductsConvert($id:ID!,$asof:DateTime!) {
                                              product(id:$id) {
                                                id,name,strength,bottleSize,alcohol{id,name},caseSize,mixedPack,containerType
                                                sites(asof:$asof) {siteId,lot,inBondBottles,dutyPaidBottles}
                                              }
                                            }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date});
      if (!results.error) {
        this.product = results.data.product;
        this.transaction.targetProductId = this.products.find(p => p.id === this.transaction.targetProductId)?.id || (this.products.length ? this.products[0].id : null);
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      this.$refs.form.validate();
      if (!this.valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      const results = await this.$apiCall(`mutation InventoryProductsConvert($input: ProductConvertInput!) {
                                            productConvertCreate(input:$input) {id}
                                          }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} product convert transaction for ${this.product.name}`});
      }
		}
	}
}
</script>